import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { FileCopy } from '@mui/icons-material';
import {
    Button,
    IconButton,
    Input,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { TFunction } from 'i18next';

import { WorkItemModelStatusEnum } from '@swagger-http';

import useToastList from '@components/notifications/toast-notifications-provider';
import TriggerDataImportDialog from '@containers/customer/smart-energy/tables/dialogs/trigger-data-import';

import { copyToClipboard } from '@tools/utils';

import styles from '@containers/customer/smart-energy/tables/components.module.scss';

export type DataImportStatus = {
    isLoadingDataImportStatus?: boolean;
    actualDataImportStatus?: WorkItemModelStatusEnum;
    siteId: string;
    configId: string; // installation ID
};

const getDataImportStatusTranslations = (t: TFunction) => ({
    running: t('customer.pvBatteryTable.components.dataImportStatus.running'),
    binding: t('customer.pvBatteryTable.components.dataImportStatus.binding'),
    pending: t('customer.pvBatteryTable.components.dataImportStatus.pending'),
    done: t('customer.pvBatteryTable.components.dataImportStatus.done'),
    no_data_found: t(
        'customer.pvBatteryTable.components.dataImportStatus.no_data_found',
    ),
    error: t('customer.pvBatteryTable.components.dataImportStatus.error'),
});

export const DeviceId = ({ deviceId }: { deviceId?: string }) => {
    const { t } = useTranslation();
    const { showToast } = useToastList();

    const handleClick = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            await copyToClipboard(deviceId!);
            showToast({
                message: t('customer.pvBatteryTable.copyDeviceIdMessage'),
                toastType: 'info',
            });
        },
        [deviceId, showToast, t],
    );

    return (
        <Stack className={styles['device-id']}>
            <Input
                readOnly
                value={deviceId}
                className={styles['device-id-textfield']}
            />
            <Tooltip
                title={t('customer.pvBatteryTable.copyDeviceId')}
                placement="top"
            >
                <IconButton
                    className={styles.fileCopyIcon}
                    onClick={handleClick}
                    data-testid="copy-device-id-button"
                >
                    <FileCopy />
                </IconButton>
            </Tooltip>
        </Stack>
    );
};

export const TriggerDataImport = (props: DataImportStatus) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const handleClick = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            setIsOpen(true);
        },
        [],
    );
    const translations = getDataImportStatusTranslations(t);

    const { isLoadingDataImportStatus, actualDataImportStatus } = props;

    const dataImportDisabled = useMemo(
        () =>
            [
                WorkItemModelStatusEnum.Binding,
                WorkItemModelStatusEnum.Running,
            ].includes(actualDataImportStatus!),
        [actualDataImportStatus],
    );

    return (
        <Stack direction="column">
            <Button
                className={styles.dataImportButton}
                variant="contained"
                onClick={handleClick}
                disabled={isLoadingDataImportStatus || dataImportDisabled}
            >
                <Typography
                    noWrap
                    sx={{
                        fontWeight: 600,
                    }}
                >
                    {t(
                        'customer.pvBatteryTable.components.dataImportStatus.dataImport',
                    )}
                </Typography>
            </Button>
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 400,
                    }}
                >
                    {t(
                        'customer.pvBatteryTable.components.dataImportStatus.currentStatus',
                    )}
                </Typography>
                <Typography
                    variant="subtitle2"
                    sx={{ textDecoration: 'underline', fontWeight: 600 }}
                >
                    {isLoadingDataImportStatus
                        ? t(
                              'customer.pvBatteryTable.components.dataImportStatus.isLoading',
                          )
                        : translations[
                              actualDataImportStatus as keyof typeof translations
                          ]}
                </Typography>
            </Stack>
            {isOpen && (
                <TriggerDataImportDialog
                    siteId={props.siteId}
                    configId={props.configId}
                    handleClose={() => setIsOpen(false)}
                />
            )}
        </Stack>
    );
};
